/**
 * Media Center — tabs, responsive pagination, URL hash sync, year filter.
 *
 * Tabs: "Press Releases" (list, always 5/page) and "Media Coverage" (cards, responsive per_page).
 * Hash: #press-releases / #media-coverage synced with active tab.
 * Year select: dynamically updated per tab from server-rendered data attributes.
 */
export default class MediaCenter {
    constructor() {
        this.block = document.querySelector('.media');
        if (!this.block) return;

        this.holder = this.block.querySelector('.media__holder');
        this.tabs = this.block.querySelectorAll('.media__tab');
        this.tabContents = this.block.querySelectorAll('.media__tab-content');
        this.yearSelect = this.block.querySelector('.media-year-select');

        this.currentType = 'press';
        this.currentYear = 'all';
        this.currentPage = 1;
        this.isLoading = false;
        this.suppressYearChange = false;

        // Fallback mapping when data-hash attributes might be missing (ACF cache).
        this.hashMap = {
            'press': 'press-releases',
            'press-releases': 'media-coverage',
        };
        this.typeByHash = {
            'press-releases': 'press',
            'media-coverage': 'press-releases',
        };

        // Year data injected by PHP via data attributes.
        this.yearsData = {
            press: JSON.parse(this.holder?.dataset.yearsPress || '[]'),
            'press-releases': JSON.parse(this.holder?.dataset.yearsMedia || '[]'),
        };

        this.init();
    }

    /**
     * Items per page based on active tab and viewport width.
     * List view (press): always 5.
     * Cards view: >=1024 → 9, 768–1023 → 10, <768 → 6.
     */
    getPerPage() {
        if (this.currentType === 'press') return 5;

        const w = window.innerWidth;
        if (w >= 1024) return 9;
        if (w >= 768) return 10;
        return 6;
    }

    init() {
        // Tab click handlers
        this.tabs.forEach(tab => {
            tab.addEventListener('click', (e) => this.handleTabClick(e, tab));
        });

        // Year filter via select2 (jQuery) or native
        if (this.yearSelect) {
            if (window.jQuery) {
                jQuery(this.yearSelect).on('change', () => {
                    if (this.suppressYearChange) return;
                    this.handleYearChange();
                });
            } else {
                this.yearSelect.addEventListener('change', () => {
                    if (this.suppressYearChange) return;
                    this.handleYearChange();
                });
            }
        }

        // Delegated pagination clicks
        this.block.addEventListener('click', (e) => {
            const btn = e.target.closest('.pagination__btn');
            if (!btn || btn.disabled) return;

            if (btn.classList.contains('pagination__btn--prev')) {
                this.currentPage = Math.max(1, this.currentPage - 1);
            } else if (btn.classList.contains('pagination__btn--next')) {
                this.currentPage++;
            } else {
                const page = parseInt(btn.textContent.trim(), 10);
                if (!isNaN(page)) this.currentPage = page;
            }

            this.fetchItems();
        });

        // Listen for external hash changes (e.g. back/forward)
        window.addEventListener('hashchange', () => this.handleHashChange());

        // Render pagination buttons for the server-rendered list tab
        this.renderInitialPagination();

        // Activate correct tab based on URL hash
        this.handleInitialHash();
    }

    /* ------------------------------------------------------------------ */
    /*  Hash management                                                    */
    /* ------------------------------------------------------------------ */

    handleInitialHash() {
        const hash = window.location.hash.replace('#', '');
        if (!hash) return;

        // Find tab by hash: first try data-hash attribute, then fallback to type mapping.
        const targetType = this.typeByHash[hash];
        if (!targetType) return;

        let tab = this.block.querySelector(`[data-hash="${hash}"]`);
        if (!tab) {
            tab = this.block.querySelector(`[data-type="${targetType}"]`);
        }
        if (tab && !tab.classList.contains('active')) {
            this.switchTab(tab, false);
        }
    }

    handleHashChange() {
        const hash = window.location.hash.replace('#', '');
        if (!hash) return;

        const activeTab = this.block.querySelector('.media__tab.active');
        const activeHash = activeTab ? this.getTabHash(activeTab) : '';
        if (hash === activeHash) return;

        const targetType = this.typeByHash[hash];
        let tab = this.block.querySelector(`[data-hash="${hash}"]`);
        if (!tab && targetType) {
            tab = this.block.querySelector(`[data-type="${targetType}"]`);
        }
        if (tab) this.switchTab(tab, false);
    }

    updateHash(hash) {
        if (!hash) return;
        if (window.location.hash.replace('#', '') !== hash) {
            history.replaceState(null, '', '#' + hash);
        }
    }

    /**
     * Resolve hash string for a tab element.
     * Uses data-hash attribute with fallback to type→hash mapping.
     */
    getTabHash(tab) {
        return tab.dataset.hash || this.hashMap[tab.dataset.type] || '';
    }

    /* ------------------------------------------------------------------ */
    /*  Tabs                                                               */
    /* ------------------------------------------------------------------ */

    handleTabClick(e, tab) {
        e.preventDefault();
        if (tab.classList.contains('active')) return;
        this.switchTab(tab, true);
    }

    switchTab(tab, shouldUpdateHash) {
        // Update active tab button
        this.tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        // Show/hide tab content
        const targetId = tab.dataset.tabOpener;
        this.tabContents.forEach(content => {
            if (content.dataset.tabContent === targetId) {
                content.classList.add('active');
                content.style.display = '';
            } else {
                content.classList.remove('active');
                content.style.display = 'none';
            }
        });

        // Update internal state
        this.currentType = tab.dataset.type;
        this.currentPage = 1;
        this.currentYear = 'all';

        // Sync URL hash
        if (shouldUpdateHash) {
            this.updateHash(this.getTabHash(tab));
        }

        // Rebuild year dropdown for this tab's post type
        this.updateYearOptions();

        // Fetch items for new tab
        this.fetchItems();
    }

    /* ------------------------------------------------------------------ */
    /*  Year filter                                                        */
    /* ------------------------------------------------------------------ */

    handleYearChange() {
        this.currentYear = this.yearSelect.value;
        this.currentPage = 1;
        this.fetchItems();
    }

    /**
     * Rebuild year select options based on the active tab's post type.
     * Uses data from PHP data attributes to avoid extra AJAX calls.
     */
    updateYearOptions() {
        if (!this.yearSelect) return;

        const years = this.yearsData[this.currentType] || [];
        this.suppressYearChange = true;

        if (window.jQuery && jQuery.fn.select2) {
            const $select = jQuery(this.yearSelect);
            $select.find('option:not([value="all"])').remove();
            years.forEach(y => $select.append(new Option(String(y), String(y))));
            $select.val('all').trigger('change');
        } else {
            // Fallback for native select
            const allOption = this.yearSelect.querySelector('option[value="all"]');
            this.yearSelect.innerHTML = '';
            if (allOption) this.yearSelect.appendChild(allOption);
            years.forEach(y => {
                const opt = document.createElement('option');
                opt.value = String(y);
                opt.textContent = String(y);
                this.yearSelect.appendChild(opt);
            });
            this.yearSelect.value = 'all';
        }

        this.suppressYearChange = false;
    }

    /* ------------------------------------------------------------------ */
    /*  AJAX fetch                                                         */
    /* ------------------------------------------------------------------ */

    fetchItems() {
        if (this.isLoading) return;
        this.isLoading = true;

        const perPage = this.getPerPage();
        const formData = new FormData();
        formData.append('action', 'filter_media_center_items');
        formData.append('nonce', typeof scripts_data !== 'undefined' ? scripts_data.media_center_nonce : '');
        formData.append('type', this.currentType);
        formData.append('year', this.currentYear);
        formData.append('page', this.currentPage);
        formData.append('per_page', perPage);

        const activeTabContent = this.block.querySelector('.media__tab-content.active');
        if (!activeTabContent) {
            this.isLoading = false;
            return;
        }

        const contentContainer = activeTabContent.querySelector('.media__content');
        const paginationNav = activeTabContent.querySelector('.media__pagination');

        if (contentContainer) contentContainer.style.opacity = '0.5';

        const ajaxUrl = typeof scripts_data !== 'undefined' ? scripts_data.ajax_url : '/wp-admin/admin-ajax.php';

        fetch(ajaxUrl, { method: 'POST', body: formData })
            .then(res => res.json())
            .then(res => {
                this.isLoading = false;
                if (contentContainer) contentContainer.style.opacity = '1';

                if (res.success) {
                    if (contentContainer) contentContainer.innerHTML = res.data.html;
                    this.renderPagination(res.data.total_pages, res.data.current_page, paginationNav);
                }
            })
            .catch(err => {
                this.isLoading = false;
                if (contentContainer) contentContainer.style.opacity = '1';
                console.error('MediaCenter fetch error:', err);
            });
    }

    /* ------------------------------------------------------------------ */
    /*  Pagination rendering                                               */
    /* ------------------------------------------------------------------ */

    /**
     * Populate pagination for the list tab on initial page load.
     * PHP already rendered the items; we just need the pagination buttons.
     */
    renderInitialPagination() {
        const listTab = this.block.querySelector('[data-tab-content="list-items"]');
        if (!listTab) return;

        const nav = listTab.querySelector('.media__pagination');
        if (!nav) return;

        const totalPages = parseInt(nav.dataset.totalPages, 10) || 0;
        this.renderPagination(totalPages, 1, nav);
    }

    /**
     * Build pagination buttons with ellipsis for the given nav container.
     */
    renderPagination(totalPages, currentPage, navContainer) {
        if (!navContainer) return;

        if (totalPages <= 1) {
            navContainer.style.display = 'none';
            navContainer.innerHTML = '';
            return;
        }

        navContainer.style.display = 'flex';
        let html = '';

        // Previous button
        html += `<button class="pagination__btn pagination__btn--prev" aria-label="Previous page" ${currentPage === 1 ? 'disabled' : ''}>
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>`;

        // Page number buttons with ellipsis
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                const isActive = i === currentPage;
                html += `<button class="pagination__btn${isActive ? ' pagination__btn--active' : ''}" ${isActive ? 'aria-current="page"' : ''}>${i}</button>`;
            } else if (i === currentPage - 2 || i === currentPage + 2) {
                html += '<span class="pagination__ellipsis" aria-hidden="true">…</span>';
            }
        }

        // Next button
        html += `<button class="pagination__btn pagination__btn--next" aria-label="Next page" ${currentPage === totalPages ? 'disabled' : ''}>
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>`;

        navContainer.innerHTML = html;
    }
}
